import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

const CustomerContext = createContext({});
export const useCustomer = () => useContext(CustomerContext);

const KEY = "tavoline.customer";

export function CustomerProvider({ children }) {
  const [slug, setSlugState] = useState(null);
  const [session, setSessionState] = useState(null);
  const [cart, setCart] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(KEY) || "null"); } catch {}
    if (saved) {
      setSlugState(saved.slug || null);
      setSessionState(saved.session || null);
      setCart(saved.cart || []);
    }
    setReady(true);
  }, []);

  const persist = useCallback((next) => {
    const payload = {
      slug: next.slug !== undefined ? next.slug : slug,
      session: next.session !== undefined ? next.session : session,
      cart: next.cart !== undefined ? next.cart : cart,
    };
    localStorage.setItem(KEY, JSON.stringify(payload));
  }, [slug, session, cart]);

  const setSlug = (s) => { setSlugState(s); persist({ slug: s }); };

  const setSession = async (s) => {
    setSessionState(s);
    setCart([]);
    persist({ session: s, cart: [] });
  };

  const addToCart = (line) => {
    setCart((prev) => {
      const next = [...prev, { ...line, key: `${line.item_id}-${Date.now()}` }];
      persist({ cart: next });
      return next;
    });
  };

  const updateQty = (key, qty) => {
    setCart((prev) => {
      const next = prev.map((l) => (l.key === key ? { ...l, quantita: qty } : l)).filter((l) => l.quantita > 0);
      persist({ cart: next });
      return next;
    });
  };

  const removeLine = (key) => {
    setCart((prev) => {
      const next = prev.filter((l) => l.key !== key);
      persist({ cart: next });
      return next;
    });
  };

  const clearCart = () => { setCart([]); persist({ cart: [] }); };

  const cartCount = cart.reduce((s, l) => s + l.quantita, 0);
  const cartTotal = cart.reduce((s, l) => s + l.quantita * l.prezzo_cent, 0);

  const sendOrder = async () => {
    if (!session) throw new Error("Nessuna sessione attiva");
    const righe = cart.map((l) => ({
      item_id: l.item_id,
      quantita: l.quantita,
      nota_riga: l.nota_riga,
      note_rapide: l.note_rapide,
    }));
    const res = await api.createOrder({ session_id: session.session_id, guest_id: session.guest_id, righe });
    clearCart();
    return res;
  };

  const leaveSession = async () => {
    setSessionState(null);
    setCart([]);
    persist({ session: null, cart: [] });
  };

  return (
    <CustomerContext.Provider
      value={{
        slug, session, cart, ready,
        setSlug, setSession, addToCart, updateQty, removeLine, clearCart,
        cartCount, cartTotal, sendOrder, leaveSession,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
}
